import React, { useState, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { api } from '../../api/client';
import { config } from '../../config/environment';
import { toast } from 'sonner';
import { 
  Plus, 
  Search, 
  Eye, 
  Edit, 
  Download, 
  RefreshCw,
  AlertCircle,
  CheckCircle,
  Trash2,
  Lock,
  Unlock,
  AlertTriangle
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter
} from '../../components/ui/dialog';

const PurchaseOrderList = ({ onNavigate }) => {
  // Helper to handle navigation - converts URL-style to view-based
  const navigate = (path) => {
    if (!onNavigate) {
      console.warn('Navigation not available');
      return;
    }
    
    if (path === '/purchase-orders/create' || path.includes('create')) {
      onNavigate('create');
    } else if (path.includes('/purchase-orders/') && path.includes('/edit')) {
      const poId = path.split('/purchase-orders/')[1]?.split('/')[0];
      onNavigate('detail', poId); // Go to detail for editing
    } else if (path.includes('/purchase-orders/')) {
      const poId = path.split('/purchase-orders/')[1];
      onNavigate('detail', poId);
    } else {
      onNavigate('list');
    }
  };
  
  const tenantId = localStorage.getItem('tenant_id') || 'tenant-123';
  
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [approvingId, setApprovingId] = useState(null); // Track which PO is being approved
  
  // Delete dialog state
  const [deleteDialog, setDeleteDialog] = useState({ open: false, po: null });
  const [unlockDialog, setUnlockDialog] = useState({ open: false, po: null });
  const [deletingId, setDeletingId] = useState(null);

  useEffect(() => {
    fetchPurchaseOrders();
  }, [statusFilter]);

  const fetchPurchaseOrders = async () => {
    setLoading(true);
    try {
      const url = statusFilter === 'all' 
        ? `/api/purchase-orders?tenant_id=${tenantId}`
        : `/api/purchase-orders?tenant_id=${tenantId}&status=${statusFilter}`;
      
      // api.get returns data directly (already unwrapped)
      const data = await api.get(url, {
        headers: { 'X-Tenant-ID': tenantId }
      });
      
      console.log('Fetched POs:', data);
      setPurchaseOrders(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching purchase orders:', error);
      setPurchaseOrders([]);
    } finally {
      setLoading(false);
    }
  };

  // One-click approve handler
  const handleApprovePO = async (poId, poNumber) => {
    setApprovingId(poId);
    try {
      await api.put(`/api/purchase-orders/${poId}`, {
        status: 'approved',
        approved_at: new Date().toISOString(),
        approved_by: 'current_user' // In production, use actual user ID
      }, {
        headers: { 'X-Tenant-ID': tenantId }
      });
      
      toast.success('✅ PO Approved!', {
        description: `${poNumber} has been approved and is ready for processing`
      });
      
      // Refresh the list
      fetchPurchaseOrders();
    } catch (error) {
      console.error('Error approving PO:', error);
      toast.error('Failed to approve PO', {
        description: error.response?.data?.detail || error.message
      });
    } finally {
      setApprovingId(null);
    }
  };

  // ============================================================
  // DELETE PO HANDLERS
  // ============================================================
  const isReceivedStatus = (status) => {
    const receivedStatuses = ['received', 'partially_received', 'fully_received'];
    return receivedStatuses.includes(status?.toLowerCase());
  };

  const handleDeleteRequest = (po) => {
    if (isReceivedStatus(po.status)) {
      // Show unlock dialog for received POs
      setUnlockDialog({ open: true, po });
    } else {
      // Show regular delete dialog
      setDeleteDialog({ open: true, po });
    }
  };

  const confirmDelete = async () => {
    const po = deleteDialog.po;
    if (!po) return;
    
    setDeletingId(po._id);
    try {
      const response = await fetch(`${config.backendUrl}/api/purchase-orders/${po._id}`, {
        method: 'DELETE',
        headers: { 
          'X-Tenant-ID': tenantId,
          'Content-Type': 'application/json'
        }
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        toast.success(`PO ${po.po_number} deleted`, {
          description: 'Purchase order has been permanently removed'
        });
        fetchPurchaseOrders(); // Refresh list
      } else {
        toast.error('Failed to delete PO', {
          description: data.detail || data.message || 'Unknown error'
        });
      }
    } catch (error) {
      console.error('Error deleting PO:', error);
      toast.error('Failed to delete PO', {
        description: error.message
      });
    } finally {
      setDeletingId(null);
      setDeleteDialog({ open: false, po: null });
    }
  };

  const confirmUnlockAndDelete = async () => {
    const po = unlockDialog.po;
    if (!po) return;
    
    setDeletingId(po._id);
    try {
      // Force delete with confirmation
      const response = await fetch(`${config.backendUrl}/api/purchase-orders/${po._id}?force_delete=true`, {
        method: 'DELETE',
        headers: { 
          'X-Tenant-ID': tenantId,
          'Content-Type': 'application/json'
        }
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        toast.success(`PO ${po.po_number} unlocked & deleted`, {
          description: 'Received PO has been permanently removed'
        });
        fetchPurchaseOrders(); // Refresh list
      } else {
        toast.error('Failed to delete PO', {
          description: data.detail || data.message || 'Unknown error'
        });
      }
    } catch (error) {
      console.error('Error deleting PO:', error);
      toast.error('Failed to delete PO', {
        description: error.message
      });
    } finally {
      setDeletingId(null);
      setUnlockDialog({ open: false, po: null });
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      draft: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
      submitted: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
      approved: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
      received: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300'
    };
    return colors[status] || colors.draft;
  };

  const filteredPOs = purchaseOrders.filter(po =>
    po.po_number?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    po.vendor_po_number?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    po.supplier?.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    po.supplier_name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="p-3 bg-[#0f1729] min-h-screen">
      {/* Compact Header */}
      <div className="flex justify-between items-center mb-3">
        <h1 className="text-lg font-semibold text-white">
          Purchase Orders
        </h1>
        <div className="flex gap-2">
          <Button
            onClick={() => navigate('/purchase-orders/create')}
            className="h-8 px-3 text-xs bg-blue-600 hover:bg-blue-700 text-white gap-1"
          >
            <Plus className="w-3 h-3" />
            New PO
          </Button>
          <Button
            onClick={fetchPurchaseOrders}
            variant="outline"
            className="h-8 w-8 p-0 border-gray-600"
            title="Refresh"
          >
            <RefreshCw className="w-3 h-3 text-gray-400" />
          </Button>
          <Button
            variant="outline"
            className="h-8 w-8 p-0 border-gray-600"
            title="Export"
          >
            <Download className="w-3 h-3 text-gray-400" />
          </Button>
        </div>
      </div>

      {/* Status Tabs - Compact */}
      <div className="flex gap-1 mb-3">
        {['all', 'draft', 'submitted', 'approved', 'received'].map((status) => (
          <button
            key={status}
            onClick={() => setStatusFilter(status)}
            className={`px-3 py-1.5 text-xs font-medium rounded transition-colors ${
              statusFilter === status
                ? 'bg-blue-600 text-white'
                : 'bg-[#1a2744] text-gray-400 hover:text-white hover:bg-[#243352]'
            }`}
          >
            {status.charAt(0).toUpperCase() + status.slice(1)}
          </button>
        ))}
      </div>

      {/* Search - Compact */}
      <div className="mb-3">
        <div className="relative">
          <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 w-3 h-3 text-gray-500" />
          <Input
            placeholder="Search PO# or supplier..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="h-8 pl-7 text-sm bg-[#1a2744] border-gray-700 text-white placeholder-gray-500"
          />
        </div>
      </div>

      {/* Table - Compact */}
      <div className="bg-[#1a2744] rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-[#0f1729]">
              <tr>
                <th className="px-3 py-2 text-left text-[10px] font-medium text-gray-400 uppercase tracking-wider">
                  PO Number
                </th>
                <th className="px-3 py-2 text-left text-[10px] font-medium text-gray-400 uppercase tracking-wider">
                  Vendor PO#
                </th>
                <th className="px-3 py-2 text-left text-[10px] font-medium text-gray-400 uppercase tracking-wider">
                  Supplier
                </th>
                <th className="px-3 py-2 text-left text-[10px] font-medium text-gray-400 uppercase tracking-wider">
                  Order Date
                </th>
                <th className="px-3 py-2 text-left text-[10px] font-medium text-gray-400 uppercase tracking-wider">
                  Delivery
                </th>
                <th className="px-3 py-2 text-left text-[10px] font-medium text-gray-400 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-3 py-2 text-right text-[10px] font-medium text-gray-400 uppercase tracking-wider">
                  Weight
                </th>
                <th className="px-3 py-2 text-right text-[10px] font-medium text-gray-400 uppercase tracking-wider">
                  Total
                </th>
                <th className="px-3 py-2 text-center text-[10px] font-medium text-gray-400 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700">
              {loading ? (
                <tr>
                  <td colSpan="9" className="px-3 py-8 text-center">
                    <div className="flex justify-center items-center">
                      <RefreshCw className="w-4 h-4 animate-spin text-blue-500" />
                      <span className="ml-2 text-gray-400 text-sm">Loading...</span>
                    </div>
                  </td>
                </tr>
              ) : filteredPOs.length === 0 ? (
                <tr>
                  <td colSpan="9" className="px-3 py-8">
                    <div className="text-center">
                      <AlertCircle className="w-8 h-8 mx-auto text-gray-500 mb-2" />
                      <h3 className="text-sm font-medium text-white mb-1">
                        No purchase orders yet
                      </h3>
                      <p className="text-gray-400 text-xs mb-3">
                        Create your first purchase order
                      </p>
                      <Button
                        onClick={() => navigate('/purchase-orders/create')}
                        className="h-7 px-3 text-xs bg-blue-600 hover:bg-blue-700"
                      >
                        <Plus className="w-3 h-3 mr-1" />
                        Create PO
                      </Button>
                    </div>
                  </td>
                </tr>
              ) : (
                filteredPOs.map((po) => (
                  <tr 
                    key={po._id} 
                    className="hover:bg-[#0f1729]/50 transition-colors"
                  >
                    <td className="px-3 py-2 whitespace-nowrap">
                      <span className="text-sm font-medium text-white">
                        {po.po_number}
                      </span>
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-400">
                      {po.vendor_po_number || '-'}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-300">
                      {po.supplier?.name || po.supplier_name || 'N/A'}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-400">
                      {po.order_date ? new Date(po.order_date).toLocaleDateString() : 'N/A'}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-400">
                      {po.expected_delivery ? new Date(po.expected_delivery).toLocaleDateString() : 'N/A'}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      <span className={`px-2 py-0.5 text-[10px] font-medium rounded ${getStatusColor(po.status)}`}>
                        {po.status?.toUpperCase()}
                      </span>
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-sm text-right text-gray-300">
                      {(() => {
                        // Calculate total weight from line items
                        const totalWeight = po.line_items?.reduce((sum, item) => {
                          if (!item.weight) return sum;
                          // Convert to KG for consistent display
                          const weightInKg = item.weight_unit === 'LB' 
                            ? item.weight / 2.20462 
                            : item.weight;
                          return sum + weightInKg;
                        }, 0) || 0;
                        
                        if (totalWeight === 0) return '-';
                        return `${totalWeight.toFixed(1)} kg`;
                      })()}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-sm text-right font-semibold text-emerald-400">
                      ${(Number(po.cost_breakdown?.total) || 0).toFixed(2)}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-center">
                      <div className="flex justify-center gap-1">
                        <button
                          onClick={() => navigate(`/purchase-orders/${po._id}`)}
                          className="p-1 text-blue-400 hover:text-blue-300"
                          title="View"
                        >
                          <Eye className="w-3.5 h-3.5" />
                        </button>
                        {po.status === 'draft' && (
                          <button
                            onClick={() => navigate(`/purchase-orders/${po._id}/edit`)}
                            className="p-1 text-gray-400 hover:text-gray-300"
                            title="Edit"
                          >
                            <Edit className="w-3.5 h-3.5" />
                          </button>
                        )}
                        {/* One-click Approve button for submitted POs */}
                        {po.status === 'submitted' && (
                          <button
                            onClick={() => handleApprovePO(po._id, po.po_number)}
                            disabled={approvingId === po._id}
                            className={`px-2 py-0.5 text-[10px] font-medium rounded flex items-center gap-1 transition-colors ${
                              approvingId === po._id
                                ? 'bg-gray-600 text-gray-400 cursor-wait'
                                : 'bg-emerald-600 hover:bg-emerald-500 text-white'
                            }`}
                            title="Approve PO"
                            data-testid={`approve-po-${po._id}`}
                          >
                            {approvingId === po._id ? (
                              <RefreshCw className="w-3 h-3 animate-spin" />
                            ) : (
                              <CheckCircle className="w-3 h-3" />
                            )}
                            Approve
                          </button>
                        )}
                        {/* Delete button with lock for received POs */}
                        {isReceivedStatus(po.status) ? (
                          <button
                            onClick={() => handleDeleteRequest(po)}
                            disabled={deletingId === po._id}
                            className="p-1 text-amber-500 hover:text-amber-400"
                            title="Locked - Click to unlock for deletion"
                            data-testid={`lock-po-${po._id}`}
                          >
                            {deletingId === po._id ? (
                              <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                            ) : (
                              <Lock className="w-3.5 h-3.5" />
                            )}
                          </button>
                        ) : (
                          <button
                            onClick={() => handleDeleteRequest(po)}
                            disabled={deletingId === po._id}
                            className="p-1 text-red-400 hover:text-red-300"
                            title="Delete PO"
                            data-testid={`delete-po-${po._id}`}
                          >
                            {deletingId === po._id ? (
                              <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                            ) : (
                              <Trash2 className="w-3.5 h-3.5" />
                            )}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      
      {/* Delete Confirmation Dialog (for non-received POs) */}
      <Dialog open={deleteDialog.open} onOpenChange={(open) => !open && setDeleteDialog({ open: false, po: null })}>
        <DialogContent className="bg-[#1a2744] border-gray-700 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-400">
              <Trash2 className="w-5 h-5" />
              Delete Purchase Order?
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <p className="text-gray-300">
              Are you sure you want to delete <strong className="text-white">{deleteDialog.po?.po_number}</strong>?
            </p>
            {deleteDialog.po?.line_items?.length > 0 && (
              <p className="text-gray-500 text-sm mt-2">
                This PO has {deleteDialog.po.line_items.length} line item(s) that will be removed.
              </p>
            )}
          </div>
          <DialogFooter className="gap-2">
            <Button 
              variant="outline" 
              onClick={() => setDeleteDialog({ open: false, po: null })}
              className="border-gray-600 text-gray-300 hover:bg-gray-700"
            >
              Cancel
            </Button>
            <Button 
              onClick={confirmDelete}
              disabled={deletingId === deleteDialog.po?._id}
              className="bg-red-600 hover:bg-red-500 text-white"
            >
              {deletingId === deleteDialog.po?._id ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Unlock & Delete Dialog (for RECEIVED POs) */}
      <Dialog open={unlockDialog.open} onOpenChange={(open) => !open && setUnlockDialog({ open: false, po: null })}>
        <DialogContent className="bg-[#1a2744] border-gray-700 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-400">
              <Lock className="w-5 h-5" />
              PO is Locked
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              This PO has received items and is protected from deletion.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <p className="text-gray-300">
              <strong className="text-white">{unlockDialog.po?.po_number}</strong> has status{' '}
              <span className="text-purple-400 font-semibold">{unlockDialog.po?.status?.toUpperCase()}</span>
            </p>
            <div className="mt-4 p-3 bg-amber-900/20 border border-amber-700/50 rounded-lg">
              <p className="text-amber-400 text-sm font-medium flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" />
                Deleting may affect:
              </p>
              <ul className="list-disc ml-6 mt-2 text-gray-400 text-sm space-y-1">
                <li>Inventory records & stock levels</li>
                <li>Cost calculations & landed costs</li>
                <li>Financial reports & audit trails</li>
              </ul>
            </div>
            <p className="mt-4 text-amber-300 text-sm">
              Are you sure you want to unlock and delete this PO?
            </p>
          </div>
          <DialogFooter className="gap-2">
            <Button 
              variant="outline" 
              onClick={() => setUnlockDialog({ open: false, po: null })}
              className="border-gray-600 text-gray-300 hover:bg-gray-700"
            >
              Cancel
            </Button>
            <Button 
              onClick={confirmUnlockAndDelete}
              disabled={deletingId === unlockDialog.po?._id}
              className="bg-amber-600 hover:bg-amber-500 text-white"
            >
              {deletingId === unlockDialog.po?._id ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                <>
                  <Unlock className="w-4 h-4 mr-2" />
                  Unlock & Delete
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PurchaseOrderList;
